import React, { useState, useEffect, useRef } from "react";
import {
  Box,
  Button,
  Collapse,
  Hidden,
  IconButton,
  MenuItem,
  Tooltip,
  Typography,
} from "@mui/material";
import {
  AddBox,
  OpenInNewRounded,
  ChevronLeftRounded,
  ChevronRightRounded,
} from "@mui/icons-material";
import { Link } from "react-router-dom";
import MaterialTable from "material-table";
import { MTableToolbar } from "material-table";
import { useSnackbar } from "notistack";
import axios from "axios";
import { getMediaURL, scrollTop } from "../../../../config/values";
import { formatPreparationTimeLabel } from "../../../../config/shopPreparationTime";
import { TableCount, TablePage } from "../../../../config/Permissions";
import { getDataTableLocalization } from "../../../../config/Translate";
import { withStyles } from "@mui/styles";
import { Styles } from "../../../../config/design/styles";
import { tableIcons } from "../../../../config/design/TableIcons";
import { defaultVariables } from "../../../../config";
import { useIsSmall } from "../../../../config/hooks/useWindowDimensions";
import { useTranslation } from "react-i18next";
import { Wallet } from "lucide-react";
import RayaField from "../../../components/elements/fields/RayaField";
import * as XLSX from "xlsx";
import {
  extractAddressAreaLabel,
  ISTANBUL_DISTRICTS,
  resolveShopAddress,
  shopMatchesDistrictFilter,
} from "../../../../config/istanbulDistricts";

const DISTRICT_FILTER_FETCH_SIZE = 5000;

const Shops = ({ classes }) => {
  const isSmall = useIsSmall();
  const { t } = useTranslation();

  const [columns] = useState([
    {
      field: "logo",
      editable: "never",
      filtering: false,
      sorting: false,
      hidden: isSmall,
      title: t("logo"),
      render: (rowData) =>
        !!rowData.logo && (
          <img alt="avatar" src={rowData.logo} className={classes.avatar} />
        ),
    },
    {
      title: t("shopName"),
      field: "name",
      filterPlaceholder: t("search"),
      sorting: false,
    },
    {
      title: t("industry"),
      field: "industry",
      filterPlaceholder: t("search"),
      sorting: false,
    },
    {
      title: "Teslimat",
      editable: "never",
      field: "transfer_by",
      sorting: false,
      filterPlaceholder: t("choose"),
      headerStyle: { width: 120, minWidth: 120, maxWidth: 120 },
      cellStyle: { width: 120, minWidth: 120, maxWidth: 120 },
      render: (rowData) => {
        if (rowData.transfer_by === "system") return "kolaydoy";
        if (rowData.transfer_by === "shop") return "satici";
        return "-";
      },
    },
    {
      title: "Komisyon",
      editable: "never",
      field: "commission",
      sorting: false,
      headerStyle: { width: 85, minWidth: 85, maxWidth: 85 },
      cellStyle: { width: 85, minWidth: 85, maxWidth: 85 },
      render: (rowData) =>
        rowData.commission !== null && rowData.commission !== undefined ?
          `${rowData.commission}%`
        : "-",
    },
    {
      title: "Address",
      editable: "never",
      field: "address",
      sorting: false,
      headerStyle: { width: 200, minWidth: 200 },
      cellStyle: { width: 200, minWidth: 200, maxWidth: 200 },
      render: (rowData) => {
        const fullAddress = rowData.address || "-";
        return (
          <Tooltip title={fullAddress} arrow placement="top">
            <Typography
              component="span"
              sx={{
                display: "block",
                maxWidth: 220,
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis",
                cursor: "help",
              }}
            >
              {fullAddress}
            </Typography>
          </Tooltip>
        );
      },
    },
    {
      title: "Yarıçapı",
      editable: "never",
      field: "radius",
      sorting: false,
      headerStyle: { width: 85, minWidth: 85, maxWidth: 85 },
      cellStyle: { width: 85, minWidth: 85, maxWidth: 85 },
      render: (rowData) =>
        rowData.radius !== null && rowData.radius !== undefined ?
          rowData.radius
        : "-",
    },
    {
      title: t("status"),
      editable: "never",
      field: "status",
      sorting: false,
      filterPlaceholder: t("choose"),
      lookup: {
        active: (
          <Typography className="table-badge active">{t("active")}</Typography>
        ),
        pending: (
          <Typography className="table-badge pending">
            {t("pending")}
          </Typography>
        ),
        inactive: (
          <Typography className="table-badge inactive">
            {t("inactive")}
          </Typography>
        ),
      },
    },
    {
      title: t("activity"),
      editable: "never",
      field: "activity",
      sorting: false,
      headerStyle: { width: 180, minWidth: 180 },
      cellStyle: { width: 180, minWidth: 180 },
      render: (rowData) => (
        <>
          <Button
            variant="outlined"
            color="secondary"
            component={Link}
            to={`/panel/manage/shops/edit/${rowData.id}`}
            endIcon={<ChevronRightRounded />}
            size="small"
            style={{ margin: 5 }}
          >
            {t("details")}
          </Button>
          {!!rowData.wallet_id && (
            <IconButton
              component={Link}
              size="small"
              to={`/panel/manage/wallets/transactions/${rowData.wallet_id}`}
            >
              <Wallet />
            </IconButton>
          )}
        </>
      ),
    },
  ]);
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [showFilters, setShowFilters] = useState(false);
  const [industries, setIndustries] = useState([]);
  const [filters, setFilters] = useState({
    transfer_by: "all",
    status: "all",
    industry_id: "all",
    address_area: "all",
    radius: "all",
  });
  const tableRef = useRef(null);
  const searchRef = useRef("");
  const { enqueueSnackbar } = useSnackbar();
  const loadData = () => {
    tableRef.current && tableRef.current.onQueryChange();
  };

  useEffect(() => {
    const fetchIndustries = async () => {
      try {
        const response = await axios.get(
          `${defaultVariables.url}/industries/get-all`,
          {
            headers: {
              Accept: "application/json",
              Authorization: `Bearer ${localStorage.getItem("ACT")}`,
            },
          },
        );
        setIndustries(response.data || []);
      } catch (error) {
        console.log(error.response);
      }
    };
    fetchIndustries();
  }, []);

  const onDelete = async (id, hasNotif = true) => {
    try {
      const loginToken = localStorage.getItem("ACT");
      let data = new FormData();
      data.append("id", id);
      await axios.post(`${defaultVariables.url}/shops/remove`, data, {
        headers: {
          Accept: "application/json",
          Authorization: `Bearer ${loginToken}`,
        },
      });
      if (hasNotif) enqueueSnackbar(t("deleteData"), { variant: "success" });
      loadData();
    } catch (error) {
      console.log(error.response);
      enqueueSnackbar(t("deleteDataFailed"), { variant: "error" });
    }
  };

  const handleExportXlsx = async () => {
    try {
      setExporting(true);
      const response = await axios.get(
        `${defaultVariables.url}/shops/quickView`,
        {
          headers: {
            Accept: "application/json",
            Authorization: `Bearer ${localStorage.getItem("ACT")}`,
          },
          params: {
            page: 1,
            per_page: 10000,
            search: searchRef.current || "",
            status: filters.status !== "all" ? filters.status : undefined,
            transfer_by:
              filters.transfer_by !== "all" ? filters.transfer_by : undefined,
            industry_id:
              filters.industry_id !== "all" ? filters.industry_id : undefined,
            radius: filters.radius !== "all" ? filters.radius : undefined,
          },
        },
      );

      const exportSource = (response.data?.data || []).filter((row) =>
        shopMatchesDistrictFilter(row, filters.address_area),
      );
      const exportRows = exportSource.map((row) => {
        const fullAddress = resolveShopAddress(row);
        return {
          "Shop ID": row.id || "-",
          "Restoran / Satıcı adı": row.name || "-",
          Address: fullAddress || "-",
          "Address Bölge": extractAddressAreaLabel(row),
          Slug: row.slug || "-",
          Sektör: t(row.industry?.name) || "-",
          "Sektör ID": row.industry_id || "-",
          "Kurye / Teslimat":
            row.transfer_by === "system" ? "kolaydoy"
            : row.transfer_by === "shop" ? "satici"
            : "-",
          "Komisyon (%)": row.commission ?? "-",
          Durum: row.status ? t(row.status) : "-",
          "Açıklama (Shop)": row.description || "-",
          "Minimum Sipariş": row.minimum_order ?? "-",
          "Hazırlık Süresi": row.preparation_time
            ? formatPreparationTimeLabel(row.preparation_time, t)
            : "-",
          "Ücretsiz Teslimat": row.has_free_delivery ? "Evet" : "Hayır",
          "Ücretsiz Teslimat Limiti": row.free_delivery_limit ?? "-",
          "Servis Yarıçapı": row.radius ?? "-",
          Popüler: row.is_popular ? "Evet" : "Hayır",
          Ekonomik: row.is_economic ? "Evet" : "Hayır",
          "Kapıda Ödeme": row.is_pod ? "Evet" : "Hayır",
          "Ortalama Puan": row.average_rate ?? "-",
          "Shop Oluşturulma": row.created_at || "-",
          "Shop Güncellenme": row.updated_at || "-",
          "Yönetici ID": row.owner?.id || "-",
          "Yönetici Ad": row.owner?.first_name || "-",
          "Yönetici Soyad": row.owner?.last_name || "-",
          "Yönetici Ad Soyad":
            [row.owner?.first_name, row.owner?.last_name]
              .filter(Boolean)
              .join(" ") || "-",
          "Yönetici E-posta": row.owner?.email || "-",
          "Yönetici Telefon": row.owner?.mobile || "-",
          "Yönetici Rol ID": row.owner?.role_id || "-",
          "Yönetici Cüzdan ID": row.owner?.wallet?.id || "-",
          "Yönetici Cüzdan Bakiye": row.owner?.wallet?.amount ?? "-",
          "Yönetici Avatar Dosya Adı": row.owner?.avatar?.name || "-",
          "Yönetici Avatar Uzantı": row.owner?.avatar?.extension || "-",
          "Yönetici Avatar URL":
            row.owner?.avatar ? getMediaURL(row.owner.avatar) : "-",
          "Shop Logo URL": row.logo ? getMediaURL(row.logo) : "-",
          "Shop Banner URL": row.banner ? getMediaURL(row.banner) : "-",
        };
      });

      if (!exportRows.length) {
        enqueueSnackbar("Export için kayıt bulunamadı.", {
          variant: "warning",
        });
        return;
      }

      const worksheet = XLSX.utils.json_to_sheet(exportRows);
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, "Shops");
      XLSX.writeFile(workbook, `shops-export-${Date.now()}.xlsx`);
      enqueueSnackbar("XLSX export hazırlandı.", { variant: "success" });
    } catch (error) {
      console.log(error.response || error);
      enqueueSnackbar("XLSX export alınamadı.", { variant: "error" });
    } finally {
      setExporting(false);
    }
  };

  const tableKey = `${filters.transfer_by}|${filters.status}|${filters.industry_id}|${filters.address_area}|${filters.radius}`;

  useEffect(() => {
    scrollTop();
  }, []);

  return (
    <>
      {loading ?
        "Loading"
      : <div className="main-table">
          <Box sx={{ mb: 2 }}>
            <Collapse in={showFilters}>
              <Box
                sx={{
                  p: 2,
                  border: "1px solid #e0e0e0",
                  borderRadius: 2,
                  backgroundColor: "#fff",
                  display: "grid",
                  gap: 1,
                  gridTemplateColumns: { xs: "1fr", md: "repeat(6, 1fr)" },
                }}
              >
                <RayaField
                  select
                  fullWidth
                  label={t("transferBy")}
                  value={filters.transfer_by}
                  onChange={(e) =>
                    setFilters((prev) => ({
                      ...prev,
                      transfer_by: e.target.value,
                    }))
                  }
                >
                  <MenuItem value="all">{t("all")}</MenuItem>
                  <MenuItem value="system">kolaydoy</MenuItem>
                  <MenuItem value="shop">satici</MenuItem>
                </RayaField>
                <RayaField
                  select
                  fullWidth
                  label={t("industry")}
                  value={filters.industry_id}
                  onChange={(e) =>
                    setFilters((prev) => ({
                      ...prev,
                      industry_id: e.target.value,
                    }))
                  }
                >
                  <MenuItem value="all">{t("all")}</MenuItem>
                  {industries.map((industry) => (
                    <MenuItem key={industry.id} value={industry.id}>
                      {t(industry.name)}
                    </MenuItem>
                  ))}
                </RayaField>
                <RayaField
                  select
                  fullWidth
                  label={t("status")}
                  value={filters.status}
                  onChange={(e) =>
                    setFilters((prev) => ({ ...prev, status: e.target.value }))
                  }
                >
                  <MenuItem value="all">{t("all")}</MenuItem>
                  <MenuItem value="active">{t("active")}</MenuItem>
                  <MenuItem value="pending">{t("pending")}</MenuItem>
                  <MenuItem value="inactive">{t("inactive")}</MenuItem>
                </RayaField>
                <RayaField
                  select
                  fullWidth
                  label="Bölge"
                  value={filters.address_area}
                  onChange={(e) =>
                    setFilters((prev) => ({
                      ...prev,
                      address_area: e.target.value,
                    }))
                  }
                >
                  <MenuItem value="all">{t("all")}</MenuItem>
                  {ISTANBUL_DISTRICTS.map((district) => (
                    <MenuItem key={district} value={district}>
                      {district}
                    </MenuItem>
                  ))}
                </RayaField>
                <RayaField
                  select
                  fullWidth
                  label="Yarıçapı"
                  value={filters.radius}
                  onChange={(e) =>
                    setFilters((prev) => ({ ...prev, radius: e.target.value }))
                  }
                >
                  <MenuItem value="all">{t("all")}</MenuItem>
                  {Array.from({ length: 11 }).map((_, n) => (
                    <MenuItem key={n} value={String(n)}>
                      {n}
                    </MenuItem>
                  ))}
                </RayaField>
                <Typography
                  component="span"
                  onClick={!exporting ? handleExportXlsx : undefined}
                  sx={{
                    height: 56,
                    minWidth: 130,
                    justifySelf: { xs: "stretch", md: "end" },
                    display: "flex",
                    alignItems: "center",
                    justifyContent: { xs: "flex-start", md: "flex-end" },
                    color: exporting ? "text.disabled" : "#2e7d32",
                    fontWeight: 700,
                    cursor: exporting ? "not-allowed" : "pointer",
                    userSelect: "none",
                  }}
                >
                  {exporting ? "Exporting..." : "Export XLSX"}
                </Typography>
              </Box>
            </Collapse>
          </Box>
          <MaterialTable
            key={tableKey}
            tableRef={tableRef}
            icons={tableIcons}
            title={
              <>
                <Link to="/panel/manage/shops/add">
                  <Hidden mdDown>
                    <Button
                      variant="contained"
                      color="secondary"
                      startIcon={<AddBox />}
                    >
                      {t("makeShop")}
                    </Button>
                  </Hidden>
                  <Hidden mdUp>
                    <IconButton color="primary">
                      <AddBox />
                    </IconButton>
                  </Hidden>
                </Link>
              </>
            }
            localization={getDataTableLocalization()}
            columns={columns}
            components={{
              Toolbar: (props) => (
                <Box sx={{ display: "flex", alignItems: "center" }}>
                  <Box sx={{ flex: 1 }}>
                    <MTableToolbar {...props} />
                  </Box>
                  <Button
                    variant={showFilters ? "contained" : "outlined"}
                    color="primary"
                    onClick={() => setShowFilters((prev) => !prev)}
                    sx={{ mr: 2 }}
                  >
                    Filter
                  </Button>
                </Box>
              ),
            }}
            data={(query) =>
              new Promise((resolve) => {
                searchRef.current = query.search || "";
                const useDistrictClientFilter = filters.address_area !== "all";
                axios
                  .get(`${defaultVariables.url}/shops/quickView`, {
                    headers: {
                      Accept: "application/json",
                      Authorization: `Bearer ${localStorage.getItem("ACT")}`,
                    },
                    params: {
                      page: useDistrictClientFilter ? 1 : query.page + 1,
                      per_page:
                        useDistrictClientFilter ?
                          DISTRICT_FILTER_FETCH_SIZE
                        : query.pageSize,
                      search: query.search,
                      status:
                        filters.status !== "all" ? filters.status : undefined,
                      transfer_by:
                        filters.transfer_by !== "all" ?
                          filters.transfer_by
                        : undefined,
                      industry_id:
                        filters.industry_id !== "all" ?
                          filters.industry_id
                        : undefined,
                      radius:
                        filters.radius !== "all" ? filters.radius : undefined,
                    },
                  })
                  .then((response) => {
                    const mapRow = (row) => ({
                      id: row.id,
                      name: row.name,
                      address: resolveShopAddress(row),
                      address_obj: row.address || null,
                      industry: t(row.industry?.name),
                      logo: getMediaURL(row.logo, "small"),
                      created_at: row.created_at,
                      status: row.status,
                      wallet_id: row.owner?.wallet?.id,
                      transfer_by: row.transfer_by,
                      commission: row.commission,
                      radius: row.radius,
                    });

                    const sourceRows = response.data.data || [];

                    if (useDistrictClientFilter) {
                      const filteredSource = sourceRows.filter((row) =>
                        shopMatchesDistrictFilter(row, filters.address_area),
                      );
                      const start = query.page * query.pageSize;
                      const pagedSource = filteredSource.slice(
                        start,
                        start + query.pageSize,
                      );
                      resolve({
                        data: pagedSource.map(mapRow),
                        page: query.page,
                        totalCount: filteredSource.length,
                      });
                      return;
                    }

                    resolve({
                      data: sourceRows.map(mapRow),
                      page: response.data.current_page - 1,
                      totalCount: response.data.total,
                    });
                  })
                  .catch((error) => console.log(error.response));
              })
            }
            options={{
              pageSize: TablePage,
              pageSizeOptions: TableCount,
              defaultExpanded: false,
              draggable: false,
              search: true,
              searchFieldAlignment: "right",
            }}
            editable={{
              onRowDelete: (oldData) =>
                new Promise((resolve) => {
                  setTimeout(() => {
                    resolve();
                    onDelete(oldData.id);
                    setData((prevData) =>
                      prevData.filter((item) => item.id !== oldData.id),
                    );
                  }, 600);
                }),
            }}
          />
        </div>
      }
    </>
  );
};
export default withStyles(Styles)(Shops);
